import { getIsTrainingProject } from "@repo/app-types/helpers/utilities";
import { selectProjectById } from "../../features/projects/projectsSlice.js";
import { useAppSelector } from "../../hooks/hook.js";
import { readStoredWorkspaceId } from "./rememberedWorkspace.js";
import { useWorkspaceOptions, WorkspaceId } from "./workspaces.js";

// The two workspaces a project can belong to.
const PROJECT_WORKSPACE_IDS: readonly WorkspaceId[] = [
  WorkspaceId.Recruiting,
  WorkspaceId.Training,
];

// A project belongs to the Training workspace when it's a training project,
// otherwise Recruiting. While the project is still loading we fall back to the
// most recently used project workspace to avoid a flicker (the entry point is
// usually the recruiting/training project list, so it already matches the
// project's type), and finally to whichever project workspace is available.
export function useProjectWorkspaceId(
  projectId: string | undefined,
): WorkspaceId {
  const project = useAppSelector((state) =>
    projectId ? selectProjectById(state, projectId) : undefined,
  );
  const options = useWorkspaceOptions();

  if (project) {
    return getIsTrainingProject(project)
      ? WorkspaceId.Training
      : WorkspaceId.Recruiting;
  }

  const availableProjectWorkspaceIds = options
    .map((option) => option.id)
    .filter((id) => PROJECT_WORKSPACE_IDS.includes(id));

  const rememberedWorkspaceId = readStoredWorkspaceId();
  if (
    rememberedWorkspaceId &&
    availableProjectWorkspaceIds.includes(rememberedWorkspaceId)
  ) {
    return rememberedWorkspaceId;
  }

  return availableProjectWorkspaceIds[0] ?? WorkspaceId.Recruiting;
}
